import React, { useEffect, useCallback, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table';
import { LinkNode } from '@lexical/link';
import Toolbar from './Toolbar';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $insertNodes } from 'lexical';

function Editor({ value, editorId, index, configState, setConfig, onChange }) {
  // State to track if this editor instance is focused
  const [isFocused, setIsFocused] = useState(false);

  const initialConfig = {
    namespace: editorId,
    onError: (error) => console.error(error),
    nodes: [LinkNode, TableNode, TableCellNode, TableRowNode],
    theme: {
      text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
      },
      link: 'text-blue-500 underline',
    },
    editable: false, // Start as non-editable
  };

  // Custom component to handle editor state and focus
  const EditorContentHandler = ({ onChange }) => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
      // Set editability based on focus state
      editor.setEditable(isFocused);
    }, [editor, isFocused]);

    useEffect(() => {
      if (value) {
        editor.update(() => {
          const parser = new DOMParser();
          const dom = parser.parseFromString(value, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          const root = $getRoot();
          root.clear();
          $insertNodes(nodes);
        });
      }
      
      const unregister = editor.registerUpdateListener(() => {
        editor.getEditorState().read(() => {
          const htmlString = $generateHtmlFromNodes(editor, null);
          // console.log(htmlString)
          onChange(htmlString);
        });
      });

      return () => {
        unregister();
      };
    }, [editor, value, onChange]);

    return null;
  };

  // const handleChange = useCallback(
  //   (htmlString) => {
  //     const updates = [...configState.updates];
  //     if (updates[index] !== htmlString) {
  //       updates[index] = htmlString;
  //       setConfig((prevConfig) => ({
  //         ...prevConfig,
  //         updates: updates,
  //       }));
  //     }
  //   },
  //   [index, configState, setConfig]
  // );

  const handleChange = useCallback(
    (htmlString) => {
      if (htmlString !== value) {
        onChange(htmlString);
      }
    },
    [onChange, value]
  );

  // Focus management handlers
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback((event) => {
    // Check if the new focus target is outside this editor
    const isLeavingEditor = !event.currentTarget.contains(event.relatedTarget);
    if (isLeavingEditor) {
      setIsFocused(false);
    }
  }, []);

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div 
        className={`max-w-2xl mx-auto my-4 bg-gray-900 text-white shadow-md border ${
          isFocused ? 'border-blue-500' : 'border-gray-700'
        } rounded-md transition-colors`}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex="-1" // Make the container focusable
      >
        <Toolbar />
        <EditorContentHandler onChange={handleChange} />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={`min-h-[200px] p-4 outline-none ${
                  isFocused ? 'bg-gray-800' : 'bg-gray-900'
                } text-white transition-colors`}
              />
            }
            placeholder={
              <div className="absolute top-[16px] left-[16px] pointer-events-none select-none text-gray-500">
                {isFocused ? 'Enter some text...' : 'Click to edit...'}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <LinkPlugin />
        <TablePlugin />
      </div>
    </LexicalComposer>
  );
}

export default Editor;